import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, { memo, ReactNode, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ContentBlock, WidgetRenderer, WidgetRenderProps } from '../types';
import { Blockquote } from './Blockquote';
import { CodeBlock } from './CodeBlock';
import { useInlineFormatter } from './InlineFormatter';
import {
  isTableSeparator,
  MarkdownTable,
  parseTableAlign,
  parseTableRow,
} from './MarkdownTable';

const CUSTOM_WIDGETS = new Set([
  'ai_bundle_handoff',
  'coaching_setup_card',
  'apdm_campaign_preview',
  'apdm_import_mapping',
  'apdm_coupon_choice',
  'apdm_coupon_campaign',
  'apdm_plan_step',
  'apdm_plan_card',
  'apdm_plan_progress',
  'apdm_plan_settled',
  'apdm_campaign_control',
  'bi_visualization',
  'bi_dashboard_choice',
  'bi_dashboard_changes',
  'bi_upload',
  'bi_insight_proposals',
  'bi_dataset_choice',
  'order_card',
  'vehicle_card',
  'workflow_progress',
  'technician_workload',
  'suggested_questions',
  'client_action',
  'chart',
  'gauge',
  'report_link',
  'facade',
  'milestone',
]);

export function parseContent(raw: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  const fenceRe = /```(\w+)?\n([\s\S]*?)```/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null = null;

  while ((match = fenceRe.exec(raw)) !== null) {
    if (match.index > lastIdx) {
      blocks.push({
        type: 'text',
        content: raw.slice(lastIdx, match.index),
      });
    }

    const lang = match[1] ?? '';
    const body = match[2].trim();

    if (lang && CUSTOM_WIDGETS.has(lang)) {
      try {
        blocks.push({
          type: 'widget',
          widget: lang,
          data: JSON.parse(body),
        });
      } catch {
        if (
          lang === 'ai_bundle_handoff' ||
          lang === 'coaching_setup_card' ||
          lang === 'apdm_campaign_preview' ||
          lang === 'apdm_import_mapping' ||
          lang === 'apdm_coupon_choice' ||
          lang === 'apdm_coupon_campaign' ||
          lang === 'apdm_plan_step' ||
          lang === 'apdm_plan_card' ||
          lang === 'apdm_plan_progress' ||
          lang === 'apdm_plan_settled' ||
          lang === 'bi_visualization' ||
          lang === 'bi_dashboard_choice' ||
          lang === 'bi_dashboard_changes' ||
          lang === 'bi_upload' ||
          lang === 'bi_insight_proposals' ||
          lang === 'bi_dataset_choice'
        )
          blocks.push({ type: 'widget', widget: lang, data: null });
        else blocks.push({ type: 'code', language: lang, content: body });
      }
    } else {
      blocks.push({ type: 'code', language: lang, content: body });
    }

    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < raw.length) {
    const tail = raw.slice(lastIdx);
    const unclosedIdx = /```(\w+)/.exec(tail)?.index ?? -1;
    if (unclosedIdx >= 0) {
      const tagMatch = /^```(\w+)/.exec(tail.slice(unclosedIdx));
      const tag = tagMatch?.[1] ?? '';
      const before = tail.slice(0, unclosedIdx);
      if (before.trim()) {
        blocks.push({ type: 'text', content: before });
      }
      if (CUSTOM_WIDGETS.has(tag)) {
        blocks.push({
          type: 'widget',
          widget: '_loading',
          data: { tag },
        });
      } else {
        blocks.push({
          type: 'text',
          content: tail.slice(unclosedIdx),
        });
      }
    } else {
      blocks.push({ type: 'text', content: tail });
    }
  }

  return blocks;
}

function useMarkdownStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        body: {
          fontSize: rpx(14),
          lineHeight: rpx(22),
          fontFamily: FontFamily.NotoSans.Regular,
        },
        h1: {
          fontSize: rpx(20),
          lineHeight: rpx(28),
          fontFamily: FontFamily.NotoSans.Bold,
          marginTop: rpx(8),
          marginBottom: rpx(4),
        },
        h2: {
          fontSize: rpx(17),
          lineHeight: rpx(24),
          fontFamily: FontFamily.NotoSans.Bold,
          marginTop: rpx(6),
          marginBottom: rpx(2),
        },
        h3: {
          fontSize: rpx(15),
          lineHeight: rpx(22),
          fontFamily: FontFamily.NotoSans.Bold,
          marginTop: rpx(4),
          marginBottom: rpx(2),
        },
        h4: {
          fontSize: rpx(14),
          lineHeight: rpx(20),
          fontFamily: FontFamily.NotoSans.Bold,
          marginTop: rpx(4),
          marginBottom: rpx(2),
        },
      }),
    [rpx],
  );
}

const MarkdownText = memo(({ text }: { text: string }) => {
  const { rpx } = useResponsiveContext();
  const renderInline = useInlineFormatter();
  const s = useMarkdownStyles();
  const lines = text.split('\n');
  const nodes: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (
      line.trim().startsWith('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const tableRows: string[][] = [];
      tableRows.push(parseTableRow(line));
      const align = parseTableAlign(lines[i + 1]);
      i++;
      i++;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableRows.push(parseTableRow(lines[i]));
        i++;
      }
      nodes.push(
        <MarkdownTable key={`t${i}`} rows={tableRows} align={align} />,
      );
      continue;
    }

    if (line.startsWith('> ') || line === '>') {
      const quoteLines: string[] = [];
      while (
        i < lines.length &&
        (lines[i].startsWith('> ') || lines[i] === '>')
      ) {
        quoteLines.push(lines[i] === '>' ? '' : lines[i].slice(2));
        i++;
      }
      nodes.push(<Blockquote key={`q${i}`} lines={quoteLines} />);
      continue;
    }

    if (line.startsWith('#### ')) {
      nodes.push(
        <Text key={i} style={[s.h4, { color: '#333' }]}>
          {renderInline(line.slice(5))}
        </Text>,
      );
    } else if (line.startsWith('### ')) {
      nodes.push(
        <Text key={i} style={[s.h3, { color: '#333' }]}>
          {renderInline(line.slice(4))}
        </Text>,
      );
    } else if (line.startsWith('## ')) {
      nodes.push(
        <Text key={i} style={[s.h2, { color: '#333' }]}>
          {renderInline(line.slice(3))}
        </Text>,
      );
    } else if (line.startsWith('# ')) {
      nodes.push(
        <Text key={i} style={[s.h1, { color: '#333' }]}>
          {renderInline(line.slice(2))}
        </Text>,
      );
    } else if (/^(\s*)[*-]\s+/.test(line)) {
      const match = /^(\s*)[*-]\s+/.exec(line)!;
      const indent = Math.floor(match[1].length / 2);
      const content = line.slice(match[0].length);
      nodes.push(
        <Text key={i} style={[s.body, { color: '#333' }]}>
          {'  '.repeat(indent + 1)}
          {'• '}
          {renderInline(content)}
        </Text>,
      );
    } else if (/^\s*\d+\.\s/.test(line)) {
      const match = /^(\s*\d+\.)\s/.exec(line)!;
      const content = line.slice(match[0].length);
      nodes.push(
        <Text key={i} style={[s.body, { color: '#333' }]}>
          {'  '}
          {match[1]} {renderInline(content)}
        </Text>,
      );
    } else if (line.trim() === '') {
      nodes.push(<View key={i} style={{ height: rpx(6) }} />);
    } else {
      nodes.push(
        <Text key={i} style={[s.body, { color: '#333' }]}>
          {renderInline(line)}
        </Text>,
      );
    }
    i++;
  }

  return <>{nodes}</>;
});

type Props = {
  content: string;
  renderWidget?: WidgetRenderer;
  widgetProps?: WidgetRenderProps;
};

export const MarkdownRenderer = memo(
  ({ content, renderWidget, widgetProps }: Props) => {
    const blocks = useMemo(() => parseContent(content), [content]);

    return (
      <View>
        {blocks.map((block, i) => {
          switch (block.type) {
            case 'text':
              return <MarkdownText key={i} text={block.content} />;
            case 'code':
              return (
                <CodeBlock
                  key={i}
                  language={block.language}
                  code={block.content}
                />
              );
            case 'widget':
              return renderWidget ? (
                <React.Fragment key={i}>
                  {renderWidget(block.widget, block.data, {
                    ...(widgetProps ?? {}),
                    blockIndex: i,
                  })}
                </React.Fragment>
              ) : null;
          }
        })}
      </View>
    );
  },
);
